import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, Stack, CircularProgress, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
    useTheme, useMediaQuery
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AnalyticsPage = () => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchStats = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('reports/dashboard-stats'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
        } catch (error) {
            console.error('Fetch stats error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C" mb={1}>Financial Analysis</Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>Real-time performance metrics and trends</Typography>

            {/* KPI Cards */}
            <Grid container spacing={isMobile ? 2 : 3} mb={4}>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <Card sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #edf2f7' }}>
                        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography color="text.secondary" variant="caption" fontWeight={700}>TOTAL REVENUE</Typography>
                                    <TrendingUpIcon sx={{ color: '#38A169' }} fontSize="small" />
                                </Box>
                                <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#263B7C">
                                    ${Number(stats?.kpis.revenue || 0).toLocaleString()}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <Card sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #edf2f7' }}>
                        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography color="text.secondary" variant="caption" fontWeight={700}>NET INCOME</Typography>
                                    <AttachMoneyIcon color="primary" fontSize="small" />
                                </Box>
                                <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color={stats?.kpis.netIncome >= 0 ? '#38A169' : '#E53E3E'}>
                                    ${Number(stats?.kpis.netIncome || 0).toLocaleString()}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <Card sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #edf2f7' }}>
                        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography color="text.secondary" variant="caption" fontWeight={700}>OUTSTANDING DEBT</Typography>
                                    <WarningAmberIcon sx={{ color: '#E53E3E' }} fontSize="small" />
                                </Box>
                                <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={800} color="#E53E3E">
                                    ${Number(stats?.kpis.outstanding || 0).toLocaleString()}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Line Chart: Collection Trends */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper elevation={0} sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '16px', border: '1px solid #edf2f7', height: isSmallMobile ? '300px' : '400px' }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>Monthly Collections</Typography>
                        {stats?.trends && stats.trends.length > 0 && (
                            <ResponsiveContainer width="100%" height="80%">
                                <LineChart data={stats.trends}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="month" axisLine={false} tickLine={false} style={{ fontSize: '0.75rem' }} />
                                    <YAxis axisLine={false} tickLine={false} style={{ fontSize: '0.75rem' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="total"
                                        stroke="#263B7C"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#263B7C' }}
                                        activeDot={{ r: 8 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Pie Chart: Payment Methods */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper elevation={0} sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '16px', border: '1px solid #edf2f7', height: isSmallMobile ? '350px' : '400px' }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>Payment Methods</Typography>
                        {stats?.methods && stats.methods.length > 0 && (
                            <ResponsiveContainer width="100%" height="80%">
                                <PieChart>
                                    <Pie
                                        data={stats.methods}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={isSmallMobile ? 40 : 60}
                                        outerRadius={isSmallMobile ? 60 : 80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {stats.methods.map((entry: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.75rem' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Heatmap/List: Top Debtors */}
                <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{ p: isSmallMobile ? 2 : 3, borderRadius: '16px', border: '1px solid #edf2f7' }}>
                        <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={3} spacing={2}>
                            <Box>
                                <Typography variant="h6" fontWeight={700}>Top Debtors (Action Required)</Typography>
                                <Typography variant="body2" color="text.secondary">Students with highest outstanding balances</Typography>
                            </Box>
                            <Button
                                variant="contained"
                                fullWidth={isSmallMobile}
                                startIcon={<DownloadIcon />}
                                onClick={() => {
                                    // PDF Download Logic
                                    import('jspdf').then(jsPDF => {
                                        import('jspdf-autotable').then(autoTable => {
                                            const doc = new jsPDF.default();
                                            const school = stats?.schoolProfile;

                                            // School Header
                                            doc.setFontSize(18);
                                            doc.setTextColor(38, 59, 124);
                                            doc.text(school?.name || "School Name", 14, 20);

                                            doc.setFontSize(10);
                                            doc.setTextColor(100);
                                            doc.text(school?.address || "School Address", 14, 28);
                                            doc.text(`Phone: ${school?.phone || "N/A"} | Email: ${school?.email || "N/A"}`, 14, 34);

                                            doc.setDrawColor(200);
                                            doc.line(14, 40, 196, 40);

                                            doc.setFontSize(14);
                                            doc.setTextColor(38, 59, 124);
                                            doc.text("Top Debtors Report", 14, 50);

                                            autoTable.default(doc, {
                                                startY: 55,
                                                head: [['Student ID', 'Name', 'Grade', 'Balance', 'Guardian', 'Phone', 'Email']],
                                                body: stats?.topDebtors?.map((s: any) => [
                                                    s.id || 'N/A',
                                                    s.name,
                                                    s.grade,
                                                    `$${s.balance.toLocaleString()}`,
                                                    s.guardianName,
                                                    s.guardianPhone,
                                                    s.guardianEmail
                                                ]),
                                                theme: 'grid',
                                                styles: { fontSize: 8 },
                                                headStyles: { fillColor: [38, 59, 124] }
                                            });

                                            doc.save('top_debtors_report.pdf');
                                        });
                                    });
                                }}
                                sx={{ bgcolor: '#263B7C', borderRadius: '8px', textTransform: 'none' }}
                            >
                                Download Report
                            </Button>
                        </Stack>

                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Student</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Grade</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Guardian</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Contact</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Balance</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats?.topDebtors?.slice(0, 5).map((student: any) => (
                                        <TableRow key={student.name}>
                                            <TableCell>
                                                <Typography variant="subtitle2" fontWeight={600}>{student.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{student.id}</Typography>
                                            </TableCell>
                                            <TableCell>{student.grade}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{student.guardianName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">{student.guardianPhone}</Typography>
                                                <Typography variant="caption" color="primary" sx={{ wordBreak: 'break-all' }}>{student.guardianEmail}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="subtitle2" fontWeight={700} color="#E53E3E">
                                                    ${student.balance.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AnalyticsPage;
